import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, HuntingToolkitGroup, HuntingToolkitSection } from "../../lib/firebase";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Plus, Edit, Trash2, Save, X } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminHuntingToolkit = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<HuntingToolkitGroup[]>([]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isCreatingSection, setIsCreatingSection] = useState<string | null>(null);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<{ groupId: string; sectionId: string } | null>(null);
  
  // Form states
  const [newGroup, setNewGroup] = useState({ title: "", description: "" });
  const [newSection, setNewSection] = useState({ title: "", description: "", command: "" });
  const [editGroup, setEditGroup] = useState({ title: "", description: "" });
  const [editSection, setEditSection] = useState({ title: "", description: "", command: "" });

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    loadGroups();

    return () => unsubscribe();
  }, [navigate]);

  const loadGroups = async () => {
    try {
      const groupsData = await methodologyService.getHuntingToolkitGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error("Failed to load hunting toolkit groups:", error);
      toast({
        title: "Error",
        description: "Failed to load hunting toolkit groups",
        variant: "destructive",
      });
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.title.trim()) {
      toast({
        title: "Error",
        description: "Group title is required",
        variant: "destructive",
      });
      return;
    }

    try {
      const groupId = await methodologyService.createHuntingToolkitGroup({
        title: newGroup.title.trim(),
        description: newGroup.description.trim(),
        sections: []
      });

      if (groupId) {
        toast({
          title: "Success",
          description: "Hunting toolkit group created successfully",
        });
        setNewGroup({ title: "", description: "" });
        setIsCreatingGroup(false);
        loadGroups();
      }
    } catch (error) {
      console.error("Failed to create group:", error);
      toast({
        title: "Error",
        description: "Failed to create hunting toolkit group",
        variant: "destructive",
      });
    }
  };

  const handleSaveGroup = async (groupId: string) => {
    if (!editGroup.title.trim()) {
      toast({
        title: "Error",
        description: "Group title is required",
        variant: "destructive",
      });
      return;
    }

    try {
      const group = groups.find(g => g.id === groupId);
      if (!group) return;

      const success = await methodologyService.saveHuntingToolkitGroup({
        ...group,
        title: editGroup.title.trim(),
        description: editGroup.description.trim()
      });

      if (success) {
        toast({
          title: "Success",
          description: "Hunting toolkit group updated successfully",
        });
        setEditingGroup(null);
        loadGroups();
      }
    } catch (error) {
      console.error("Failed to save group:", error);
      toast({
        title: "Error",
        description: "Failed to update hunting toolkit group",
        variant: "destructive",
      });
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!confirm("Are you sure you want to delete this group? This action cannot be undone.")) {
      return;
    }

    try {
      const success = await methodologyService.deleteHuntingToolkitGroup(groupId);
      if (success) {
        toast({
          title: "Success",
          description: "Hunting toolkit group deleted successfully",
        });
        loadGroups();
      }
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast({
        title: "Error",
        description: "Failed to delete hunting toolkit group",
        variant: "destructive",
      });
    }
  };

  const handleCreateSection = async (groupId: string) => {
    if (!newSection.title.trim() || !newSection.command.trim()) {
      toast({
        title: "Error",
        description: "Section title and command are required",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await methodologyService.addSectionToGroup(groupId, {
        title: newSection.title.trim(),
        description: newSection.description.trim(),
        command: newSection.command.trim()
      });

      if (result) {
        toast({
          title: "Success",
          description: "Section added successfully",
        });
        setNewSection({ title: "", description: "", command: "" });
        setIsCreatingSection(null);
        loadGroups();
      }
    } catch (error) {
      console.error("Failed to create section:", error);
      toast({
        title: "Error",
        description: "Failed to add section",
        variant: "destructive",
      });
    }
  };

  const handleSaveSection = async (groupId: string, sectionId: string) => {
    if (!editSection.title.trim() || !editSection.command.trim()) {
      toast({
        title: "Error",
        description: "Section title and command are required",
        variant: "destructive",
      });
      return;
    }

    try {
      const success = await methodologyService.updateSectionInGroup(groupId, sectionId, {
        title: editSection.title.trim(),
        description: editSection.description.trim(),
        command: editSection.command.trim()
      });

      if (success) {
        toast({
          title: "Success",
          description: "Section updated successfully",
        });
        setEditingSection(null);
        loadGroups();
      }
    } catch (error) {
      console.error("Failed to save section:", error);
      toast({
        title: "Error",
        description: "Failed to update section",
        variant: "destructive",
      });
    }
  };

  const handleDeleteSection = async (groupId: string, sectionId: string) => {
    if (!confirm("Are you sure you want to delete this section? This action cannot be undone.")) {
      return;
    }

    try {
      const success = await methodologyService.deleteSectionFromGroup(groupId, sectionId);
      if (success) {
        toast({
          title: "Success",
          description: "Section deleted successfully",
        });
        loadGroups();
      }
    } catch (error) {
      console.error("Failed to delete section:", error);
      toast({
        title: "Error",
        description: "Failed to delete section",
        variant: "destructive",
      });
    }
  };

  const startEditGroup = (group: HuntingToolkitGroup) => {
    setEditGroup({ title: group.title, description: group.description || "" });
    setEditingGroup(group.id);
  };

  const startEditSection = (section: HuntingToolkitSection, groupId: string) => {
    setEditSection({ 
      title: section.title, 
      description: section.description, 
      command: section.command 
    });
    setEditingSection({ groupId, sectionId: section.id });
  };

  const cancelEdit = () => {
    setEditingGroup(null);
    setEditingSection(null);
    setIsCreatingGroup(false);
    setIsCreatingSection(null);
    setNewGroup({ title: "", description: "" });
    setNewSection({ title: "", description: "", command: "" });
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/aAyuiOcvMq/dashboard')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={() => auth.signOut().then(() => navigate('/aAyuiOcvMq'))}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Hunting Toolkit Management
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Create and manage hunting toolkit groups and sections
            </p>
          </div>

          {/* Create New Group */}
          {!isCreatingGroup ? (
            <Button
              onClick={() => setIsCreatingGroup(true)}
              className="mb-6 bg-[#daed6e] hover:bg-[#c5d85c] text-black"
            >
              <Plus size={18} className="mr-2" />
              Create New Group
            </Button>
          ) : (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Create New Group</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="group-title">Group Title *</Label>
                  <Input
                    id="group-title"
                    value={newGroup.title}
                    onChange={(e) => setNewGroup({ ...newGroup, title: e.target.value })}
                    placeholder="e.g., Subdomain Enumeration"
                  />
                </div>
                <div>
                  <Label htmlFor="group-description">Description</Label>
                  <Input
                    id="group-description"
                    value={newGroup.description}
                    onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                    placeholder="Optional description for the group"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateGroup} className="bg-[#daed6e] hover:bg-[#c5d85c] text-black">
                    <Save size={18} className="mr-2" />
                    Create Group
                  </Button>
                  <Button variant="outline" onClick={cancelEdit}>
                    <X size={18} className="mr-2" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Groups List */}
          <div className="space-y-6">
            {groups.map((group) => (
              <Card key={group.id}>
                {editingGroup === group.id ? (
                  // Edit Group Form
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <Label htmlFor="edit-group-title">Group Title *</Label>
                      <Input
                        id="edit-group-title"
                        value={editGroup.title}
                        onChange={(e) => setEditGroup({ ...editGroup, title: e.target.value })}
                        placeholder="e.g., Subdomain Enumeration"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-group-description">Description</Label>
                      <Input
                        id="edit-group-description"
                        value={editGroup.description}
                        onChange={(e) => setEditGroup({ ...editGroup, description: e.target.value })}
                        placeholder="Optional description for the group"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleSaveGroup(group.id)} className="bg-[#daed6e] hover:bg-[#c5d85c] text-black">
                        <Save size={18} className="mr-2" />
                        Save Group
                      </Button>
                      <Button variant="outline" onClick={cancelEdit}>
                        <X size={18} className="mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                ) : (
                  // Normal Group Display
                  <>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{group.title}</CardTitle>
                          {group.description && (
                            <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => startEditGroup(group)}><Edit size={16} className="mr-1" />Edit</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDeleteGroup(group.id)}><Trash2 size={16} className="mr-1" />Delete</Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-medium">Sections ({group.sections.length})</h4>
                          {!isCreatingSection && (
                            <Button
                              onClick={() => setIsCreatingSection(group.id)}
                              size="sm"
                              className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                            >
                              <Plus size={16} className="mr-1" />
                              Add Section
                            </Button>
                          )}
                        </div>

                        {/* Create New Section */}
                        {isCreatingSection === group.id && (
                          <Card className="mb-4 border-2 border-dashed">
                            <CardContent className="pt-4 space-y-4">
                              <div>
                                <Label htmlFor="section-title">Section Title *</Label>
                                <Input
                                  id="section-title"
                                  value={newSection.title}
                                  onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                                  placeholder="e.g., Basic subdomain discovery"
                                />
                              </div>
                              <div>
                                <Label htmlFor="section-description">Description</Label>
                                <Input
                                  id="section-description"
                                  value={newSection.description}
                                  onChange={(e) => setNewSection({ ...newSection, description: e.target.value })}
                                  placeholder="e.g., Discovers subdomains using subfinder with recursive enumeration and saves results to a file."
                                />
                              </div>
                              <div>
                                <Label htmlFor="section-command">Command *</Label>
                                <Input
                                  id="section-command"
                                  value={newSection.command}
                                  onChange={(e) => setNewSection({ ...newSection, command: e.target.value })}
                                  placeholder="e.g., subfinder -d example.com -all -recursive > subexample.com.txt"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  onClick={() => handleCreateSection(group.id)}
                                  size="sm"
                                  className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                                >
                                  <Save size={16} className="mr-1" />
                                  Add Section
                                </Button>
                                <Button 
                                  onClick={cancelEdit}
                                  size="sm"
                                  variant="outline"
                                >
                                  <X size={16} className="mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Sections List */}
                        <div className="space-y-4">
                          {group.sections.map((section) => (
                            <Card key={section.id} className="border border-gray-200">
                              {editingSection && editingSection.groupId === group.id && editingSection.sectionId === section.id ? (
                                // Edit Section Form
                                <CardContent className="pt-4 space-y-4">
                                  <div>
                                    <Label htmlFor="edit-section-title">Section Title *</Label>
                                    <Input
                                      id="edit-section-title"
                                      value={editSection.title}
                                      onChange={(e) => setEditSection({ ...editSection, title: e.target.value })}
                                      placeholder="e.g., Basic subdomain discovery"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="edit-section-description">Description</Label>
                                    <Input
                                      id="edit-section-description"
                                      value={editSection.description}
                                      onChange={(e) => setEditSection({ ...editSection, description: e.target.value })}
                                      placeholder="e.g., Discovers subdomains using subfinder with recursive enumeration and saves results to a file."
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="edit-section-command">Command *</Label>
                                    <Input
                                      id="edit-section-command"
                                      value={editSection.command}
                                      onChange={(e) => setEditSection({ ...editSection, command: e.target.value })}
                                      placeholder="e.g., subfinder -d example.com -all -recursive > subexample.com.txt"
                                    />
                                  </div>
                                  <div className="flex gap-2">
                                    <Button 
                                      onClick={() => handleSaveSection(group.id, section.id)}
                                      size="sm"
                                      className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                                    >
                                      <Save size={16} className="mr-1" />
                                      Save Section
                                    </Button>
                                    <Button 
                                      onClick={cancelEdit}
                                      size="sm"
                                      variant="outline"
                                    >
                                      <X size={16} className="mr-1" />
                                      Cancel
                                    </Button>
                                  </div>
                                </CardContent>
                              ) : (
                                // Normal Section Display
                                <>
                                  <CardHeader>
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <CardTitle className="text-base">{section.title}</CardTitle>
                                        {section.description && (
                                          <p className="text-xs text-gray-600 mt-1">{section.description}</p>
                                        )}
                                      </div>
                                      <div className="flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => startEditSection(section, group.id)}><Edit size={14} className="mr-1" />Edit</Button>
                                        <Button size="sm" variant="destructive" onClick={() => handleDeleteSection(group.id, section.id)}><Trash2 size={14} className="mr-1" />Delete</Button>
                                      </div>
                                    </div>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="bg-black text-green-400 p-3 rounded text-xs font-mono overflow-x-auto">
                                      <code>{section.command}</code>
                                    </div>
                                  </CardContent>
                                </>
                              )}
                            </Card>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </>
                )}
              </Card>
            ))}
          </div>

          {groups.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No hunting toolkit groups created yet.</p>
              <p className="text-sm text-gray-400 mt-1">Create your first group to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 